'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState, useRef } from 'react';

import {
  UiDoctorFilterBase,
  DoctorFilterItem
} from '@/fo/ui/doctor/filter/base';
import { UiDoctorListBase } from '@/fo/ui/doctor/list/base';
import { UiDoctorMapsBase } from '@/fo/ui/doctor/maps/base';
import { UiDoctorSearchBase } from '@/fo/ui/doctor/search/base';
import { UiKitBreadcrumbsBaseProps } from '@/fo/ui/kit/breadcrumbs/base';

import { Doctor, Clinic } from '../types';

import { styles } from './UiDoctorLayoutPage.styles';

export interface UiDoctorLayoutPageProps {
  doctors: Doctor[];
  mapDoctors?: Doctor[];
  breadcrumbs: UiKitBreadcrumbsBaseProps['items'];
  banner: React.ReactNode;
  filterItems?: DoctorFilterItem[];
  city: string;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
  searchValue?: string;
  selectedFilters?: string[];
  onSearchChange?: (value: string) => void;
  onFilterChange?: (selected: string[]) => void;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  onLoadMore?: () => void;
  bothers?: DoctorFilterItem[];
  cities?: DoctorFilterItem[];
  selectedCities?: string[];
  onCityChange?: (selected: string[]) => void;
  openedTab?: 'list' | 'map';
  initialZoom?: number;
  onTabChange?: (tab: 'list' | 'map') => void;
}

const tabsMap: {
  [key in Required<UiDoctorLayoutPageProps>['openedTab']]: number;
} = {
  list: 0,
  map: 1
};

export function UiDoctorLayoutPage({
  doctors,
  mapDoctors,
  banner,
  city,
  pagination,
  searchValue: externalSearchValue,
  selectedFilters: externalSelectedFilters,
  onSearchChange,
  onFilterChange,
  onPageChange,
  loading = false,
  onLoadMore,
  bothers = [],
  cities = [],
  selectedCities: externalSelectedCities,
  onCityChange,
  openedTab = 'list',
  initialZoom,
  onTabChange
}: UiDoctorLayoutPageProps) {
  const slots = styles();
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState(tabsMap[openedTab] ?? 0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const prevPageRef = useRef<number | null>(pagination?.currentPage ?? null);

  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [internalSelectedFilters, setInternalSelectedFilters] = useState<
    string[]
  >([]);
  const [internalSelectedCities, setInternalSelectedCities] = useState<
    string[]
  >([]);

  const searchValue = externalSearchValue ?? internalSearchValue;
  const selectedFilters = externalSelectedFilters ?? internalSelectedFilters;
  const selectedCities = externalSelectedCities ?? internalSelectedCities;

  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchValue(value);
    }
  };

  const handleFilterChange = (selected: string[]) => {
    if (onFilterChange) {
      onFilterChange(selected);
    } else {
      setInternalSelectedFilters(selected);
    }
  };

  const handleCityChange = (selected: string[]) => {
    if (onCityChange) {
      onCityChange(selected);
    } else {
      setInternalSelectedCities(selected);
    }
  };

  const handleAddressClick = (doctor: Doctor, clinic: Clinic) => {
    setSelectedDoctor(doctor);
    setSelectedClinic(clinic);
    setActiveTab(1);
    tabsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePageChange = (page: number) => {
    onPageChange?.(page);
  };

  useEffect(() => {
    if (!pagination?.currentPage) {
      return;
    }

    const prevPage = prevPageRef.current;
    prevPageRef.current = pagination.currentPage;

    if (prevPage === null || prevPage === pagination.currentPage) {
      return;
    }

    filtersRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [pagination?.currentPage]);

  return (
    <div className={slots.base()}>
      {banner}
      <div className={slots.top()}>
        <div
          className={slots.tabs()}
          ref={tabsRef}
        >
          <div
            className={slots.tab({ active: activeTab === 0 })}
            onClick={() => {
              setActiveTab(0);
              onTabChange?.('list');
            }}
          >
            {t('As a list')}
          </div>
          <div
            className={slots.tab({ active: activeTab === 1 })}
            onClick={() => {
              setActiveTab(1);
              onTabChange?.('map');
            }}
          >
            {t('On the map')}
          </div>
        </div>
      </div>
      <div
        className={slots.filters()}
        ref={filtersRef}
      >
        <div className={slots.filter()}>
          <UiDoctorFilterBase
            items={bothers}
            selected={selectedFilters}
            onChange={handleFilterChange}
          />
        </div>

        {cities.length > 0 && (
          <div className={slots.filter()}>
            <UiDoctorFilterBase
              items={cities}
              selected={selectedCities}
              onChange={handleCityChange}
              minSelected={1}
              placeholder={t('City')}
            />
          </div>
        )}

        <div className={slots.search()}>
          <UiDoctorSearchBase
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={t('Find a doctor or clinic')}
            className={slots.searchInput()}
          />
        </div>
      </div>
      <div className={slots.content()}>
        {activeTab === 0 && (
          <UiDoctorListBase
            doctors={doctors}
            onAddressClick={handleAddressClick}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLoadMore={onLoadMore}
            loading={loading}
            bothers={bothers}
          />
        )}
        {activeTab === 1 && (
          <UiDoctorMapsBase
            city={city}
            doctors={mapDoctors ?? doctors}
            selectedDoctor={selectedDoctor}
            selectedClinic={selectedClinic}
            initialZoom={initialZoom}
          />
        )}
      </div>
    </div>
  );
}
